import { Locales, locales, regions } from "@/i18n/routing";

export function isInHomePage(path: string) {
  const regex = new RegExp("^\/[^\/]+-[^\/]+\/$");
  return regex.test(path);
}
/**
 * This function is used to get lang from a locale
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getLangFromLocale = (locale: string): "en" | "ar" => {
  return locale ? (locale.split("-")[1] as "en" | "ar") : "en";
};

/**
 * This function is used to get country code from a locale
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getCountryCodeFromLocale = (locale: string): string => {
  return locale ? locale.split("-")[0] : "";
};

/**
 * This function is used to get layout direction from a locale
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getDirFromLocale = (locale: string): "ltr" | "rtl" => {
  const lang = getLangFromLocale(locale);
  return lang === "ar" ? "rtl" : "ltr";
};

/**
 * This function is used to get alpa3code of a country from a locale
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getCountryAlpha3CodeFromLocale = (locale: string): string | undefined => {
  const code = getCountryCodeFromLocale(locale);
  const region = regions.find((region) => region.code === code);
  return region?.alpha3Code;
};

export const getProductChannel = (locale: string) => {
  const brandId = `${process.env.BRAND_ID ?? ""}`;
  return `${brandId}-${getCountryCodeFromLocale(locale)}-web`;
};

export const getChannelIdClient = (locale: string) => {
  const brandId = `${process.env.NEXT_PUBLIC_BRAND_ID ?? ""}`;
  return `${brandId}-${getCountryCodeFromLocale(locale)}-web`;
};

export const getFieldValue = (
  lang: "en" | "ar",
  value?: string,
  translatedValue?: string | null,
) => {
  if (translatedValue === undefined || translatedValue === null) return value;
  return lang === "en" ? value : translatedValue;
};
/**
 * This function is used to get brand id
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getContentId = (locale: string): string => {
  const country = getCountryAlpha3CodeFromLocale(locale);
  return `${process.env.BRAND_ID ?? ""}-${country}`;
};

export const fillSlot = (string: string, slotName: string, value: any) => {
  return string.replace(slotName, value);
};
export interface LinkContent {
  type: "internal" | "external";
  url: string;
}

/**
 * This function is used to construct sanity link with locale
 *
 * @param {string} link
 * @param {string} locale
 * @return {*}  {string}
 */
export const constructSanityInternalLink = (link: string, locale: string): string => {
  if (
    locales?.includes(link?.split("/")?.[0] as Locales) ||
    locales?.includes(link?.split("/")?.[1] as Locales)
  ) {
    const invalidLocale = link?.split("/")?.[0] || link?.split("/")?.[1];
    return link.replace(`${invalidLocale}`, locale);
  }
  if (link?.startsWith("/")) {
    return `/${locale}${link}`;
  }
  return link ? `/${locale}/${link}` : "";
};

export const constructUrl = (link: LinkContent | undefined, locale: string) =>
  link?.type === "external" ? link?.url : constructSanityInternalLink(link?.url ?? "", locale);

let __navigationalLocales:Locales[] = [];
export function getNavigationalLocales(): Locales[] {
    if(__navigationalLocales.length) {
        return __navigationalLocales;
    }
    else {
        try {
            const nonNavigationalCountries = process?.env?.NEXT_PUBLIC_NON_NAVIGATIONAL_COUNTRIES?.split(",") || [];
            __navigationalLocales = locales.filter(
                loc => {
                    const country = loc.split("-")[0];
                    return !(nonNavigationalCountries.includes(country));
                }
            );
        }
        catch(err) {
            console.warn("Error parsing non-navgational locales:", err);
            __navigationalLocales = [...locales];
        }
        return __navigationalLocales;
    }
}

  // export const getFieldValue = (lang: "en" | "ar", value?: string, translatedValue?: string) => {
//   return lang === "en" ? value : translatedValue;
// };

export function formatCurrency(amount: string) {
  return parseFloat(amount)
    .toFixed(2) // Ensure two decimal places
    .replace(/\B(?=(\d{3})+(?!\d))/g, ","); // Add commas
}

export function formatEpoch(epochTime: number) {
    return new Date(epochTime).toLocaleString();
}

/**
 * Creates a debounced version of a function that delays its execution
 * until after a specified delay has elapsed since the last time it was invoked.
 *
 * @template T - The type of the function to debounce.
 * @param {T} func - The function to debounce.
 * @param {number} delay - The delay in milliseconds to wait before calling the function.
 * @returns {(...args: Parameters<T>) => void} - A debounced version of the provided function.
 *
 * @example
 * // Create a debounced function with a 300ms delay
 * const debouncedLog = debounce((message) => console.log(message), 300);
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timer: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);

    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

export const getChannelIdInClient = (locale: string) => {
  const channel = process.env.NEXT_PUBLIC_BRAND_ID
    ? `${process.env.NEXT_PUBLIC_BRAND_ID}-${getCountryCodeFromLocale(locale)}-web`
    : "";
  return channel;
};

/**
 * Returns true when the locale belongs to a Flow-enabled market (e.g. ae-en / sa-ar).
 * All Checkout.com Flow logic is gated behind this check so other markets
 * are completely unaffected.
 *
 * Optionally driven by NEXT_PUBLIC_CHECKOUT_FLOW_CHANNELS (comma-separated
 * country codes, e.g. "ae,sa") so it can be toggled per deployment without a
 * code change.
 */
export const isFlowChannel = (locale: string): boolean => {
  const country = getCountryCodeFromLocale(locale);
  const allowedCountries = process.env.NEXT_PUBLIC_CHECKOUT_FLOW_CHANNELS
    ? process.env.NEXT_PUBLIC_CHECKOUT_FLOW_CHANNELS.split(",").map((c) => c.trim().toLowerCase())
    : ["ae", "sa"];
  return allowedCountries.includes(country.toLowerCase());
};

// write a function to get cookies in client side by name
export const getClientCookie = (name: string) => {
  const cookies = document.cookie.split("; ");
  const cookie = cookies.find((cookie) => cookie.startsWith(name));
  return cookie ? cookie.split("=")[1] : undefined;
};

//a function to get discount percentage provided original price and after discount price
export function handleGetDiscount(originalPrice: number, newPrice: number) {
  const discount = 100 - Math.round((newPrice / originalPrice) * 100);
  return discount > 0 ? String(discount + ".00") : "";
}

export function isNonTransactionalCountry(locale: string) {
  if (!locale) return false;
  const country = getCountryCodeFromLocale(locale);
  return (process?.env?.NEXT_PUBLIC_NON_TRANSACTIONAL_COUNTRIES?.split(",") || []).includes(
    country,
  );
}

/**
 * renderCurrencyFontIconsInHTML - Replaces currency codes in a string with HTML spans
 * using the project's custom font-based glyphs.
 */
export { renderCurrencyFontIconsInHTML, formatPhoneValueWithMask } from "@/apiUtils/commonUtils";

/**
 * Check if current environment is production.
 */
export const isProductionEnvironment = (): boolean => {
  const env = (process.env.SYSTEM_ENV || process.env.ENVIRONMENT || "").toLowerCase();
  return env === "prod" || env === "production";
};

